import { useState } from 'react'
import { SelectField } from '@/components/SelectField'

/* ---------- helpers ---------- */

function Section ({
	title,
	children
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-16'>
			<h3 className='spaces:text-14 spaces:font-semi spaces:text-content-3 spaces:uppercase spaces:tracking-wide'>
				{title}
			</h3>
			{children}
		</div>
	)
}

/* ---------- data ---------- */

const bedroomOptions = [
	{ value: 'studio', label: 'Studio' },
	{ value: '1br', label: '1 Bedroom' },
	{ value: '2br', label: '2 Bedrooms' },
	{ value: '3br', label: '3 Bedrooms' },
	{ value: '4br', label: '4 Bedrooms' }
]

const floorOptions = [
	{ value: '1', label: 'Floor 1' },
	{ value: '2', label: 'Floor 2' },
	{ value: '3', label: 'Floor 3' },
	{ value: '4', label: 'Floor 4' },
	{ value: '5', label: 'Floor 5' },
	{ value: '6', label: 'Floor 6' },
	{ value: '7', label: 'Floor 7' },
	{ value: '8', label: 'Floor 8' }
]

const sortOptions = [
	{ value: 'pr_asc', label: 'Price (Low to High)' },
	{ value: 'pr_desc', label: 'Price (High to Low)' },
	{ value: 'sf_asc', label: 'Size (Low to High)' },
	{ value: 'sf_desc', label: 'Size (High to Low)' },
	{ value: 'dt_asc', label: 'Availability' }
]

/* ---------- sandbox ---------- */

export function SelectFieldSandbox () {
	const [bedroomValue, setBedroomValue] = useState<string | undefined>(undefined)
	const [floorValue, setFloorValue] = useState<string | undefined>(undefined)
	const [sortValue, setSortValue] = useState<string>('pr_asc')

	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-32'>
			{/* ── Basic (no label) ── */}
			<Section title='Basic (No Label)'>
				<div className='spaces:flex spaces:flex-col spaces:gap-8'>
					<SelectField
						options={bedroomOptions}
						value={bedroomValue}
						onValueChange={setBedroomValue}
						placeholder='Select bedrooms…'
					/>
					<span className='spaces:text-12/16 spaces:font-default spaces:text-content-3'>
						Value: {bedroomValue ?? '—'}
					</span>
				</div>
			</Section>

			{/* ── With inline label ── */}
			<Section title='With Inline Label'>
				<SelectField
					label='Bedrooms'
					options={bedroomOptions}
					value={bedroomValue}
					onValueChange={setBedroomValue}
					placeholder='Any'
				/>
			</Section>

			{/* ── Pre-selected value ── */}
			<Section title='Pre-selected Value'>
				<div className='spaces:flex spaces:flex-col spaces:gap-8'>
					<SelectField
						label='Sort by'
						options={sortOptions}
						value={sortValue}
						onValueChange={setSortValue}
					/>
					<span className='spaces:text-12/16 spaces:font-default spaces:text-content-3'>
						Value: {sortValue}
					</span>
				</div>
			</Section>

			{/* ── With caret icon ── */}
			<Section title='With Caret Icon'>
				<SelectField
					label='Floor'
					options={floorOptions}
					value={floorValue}
					onValueChange={setFloorValue}
					placeholder='Any floor'
					showCaret
				/>
			</Section>

			{/* ── Long list (scrollable) ── */}
			<Section title='Long List (Scrollable)'>
				<div className='spaces:flex spaces:flex-col spaces:gap-8'>
					<SelectField
						options={floorOptions}
						value={floorValue}
						onValueChange={setFloorValue}
						placeholder='Select floor…'
						showCaret
					/>
					<span className='spaces:text-12/16 spaces:font-default spaces:text-content-3'>
						Value: {floorValue ?? '—'}
					</span>
				</div>
			</Section>
		</div>
	)
}
